import { useState, useEffect, useMemo } from "react";

export class HelpNavigation {
  private previousHash: string = "";
  private onChangeCallback?: (isOpen: boolean) => void;

  openHelp(): void {
    // Save current hash before opening help
    const currentHash = window.location.hash;
    if (currentHash && currentHash !== "#Help") {
      this.previousHash = currentHash;
    }
    window.history.pushState(null, "", "#Help");
    this.notifyChange(true);
  }

  closeHelp(): void {
    // Restore previous hash if it existed, otherwise clear
    if (window.location.hash === "#Help") {
      const hashToRestore = this.previousHash || "#";
      window.history.pushState(null, "", hashToRestore);
      this.previousHash = "";
    }
    this.notifyChange(false);
  }

  isHelpOpen(): boolean {
    return window.location.hash === "#Help";
  }

  /**
   * Set up listener for browser back/forward navigation
   * Returns cleanup function
   */
  setupHashChangeListener(callback: (isOpen: boolean) => void): () => void {
    this.onChangeCallback = callback;

    const handleHashChange = () => {
      const isOpen = this.isHelpOpen();
      callback(isOpen);
    };

    window.addEventListener("hashchange", handleHashChange);
    return () => {
      window.removeEventListener("hashchange", handleHashChange);
      this.onChangeCallback = undefined;
    };
  }

  reset(): void {
    this.previousHash = "";
  }

  private notifyChange(isOpen: boolean): void {
    this.onChangeCallback?.(isOpen);
  }
}

/**
 * React hook for managing help page navigation
 * Handles URL routing, browser history, and state management
 */
export function useHelpNavigation() {
  const helpNav = useMemo(() => new HelpNavigation(), []);
  const [isOpen, setIsOpen] = useState(() => helpNav.isHelpOpen());

  // Set up hash change listener for browser back/forward
  useEffect(() => {
    return helpNav.setupHashChangeListener(setIsOpen);
  }, [helpNav]);

  const openHelp = () => {
    helpNav.openHelp();
    setIsOpen(true);
  };

  const closeHelp = () => {
    helpNav.closeHelp();
    setIsOpen(false);
  };

  return {
    isOpen,
    openHelp,
    closeHelp,
  };
}
